import { useRef, useState } from 'react';
import { Modal, Form, Select, Tooltip } from 'antd';
import { useIntl } from 'react-intl';

import {
  DESCRIPTION_MAX_LENGTH,
  MATERIAL_CATEGORIES,
  NAME_MAX_LENGTH
} from 'constants/packaging';
import { NUMBER_TEXT } from 'constants/form';
import { arabicNameRule, englishNameRule } from 'utils/forms';
import { formatImages } from 'utils/upload';
import { requiredRule } from 'forms';
import { imageUpload } from 'services/upload';
import { createMaterial, getMaterialImageUploadUrl } from 'services/packaging';
import { getCurrency } from 'common/countries/countries-mapping';

import BRButton from 'components/BRButton/BRButton';
import BRFormInputs from 'components/BRFormInputs/BRFormInputs';
import UploadAttachment from 'components/SupportTickets/components/UploadAttachment/UploadAttachment';
import { notify } from 'components/Notify/Notify';

import { ReactComponent as TooltipIcon } from 'assets/bosta-icons/Tooltip.svg';

import './AddEditMaterialModal.less';

const AddEditMaterialModal = ({ close, onSuccess, ...props }) => {
  const [isLoading, setIsLoading] = useState(false);

  const intl = useIntl();
  const formRef = useRef(null);

  const onFinish = async (values) => {
    const payload = {
      ...values
    };

    if (payload.maxLimit) {
      payload.maxLimit = Number(payload.maxLimit);
    } else {
      delete payload.maxLimit;
      payload.isMaxLimitEnabled = false;
    }

    setIsLoading(true);

    try {
      const { data } = await getMaterialImageUploadUrl({
        itemName: payload.englishName
      });

      const { url, imageKey } = data;

      const formattedImage = formatImages(payload.imageKey)[0];
      await imageUpload(url, formattedImage);

      payload.imageKey = imageKey;

      await createMaterial(payload);

      notify(
        intl.formatMessage({
          id: 'packaging.manage_materials.add_material.success_message'
        }),
        'success'
      );
      onSuccess();
      close();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      {...props}
      onCancel={close}
      title={intl.formatMessage({
        id: 'packaging.manage_materials.add_material.modal_title'
      })}
      footer={
        <>
          <BRButton
            className="button-md"
            label={intl.formatMessage({ id: 'common.cancel' })}
            onClick={close}
            disabled={isLoading}
          />
          <BRButton
            className="button-md"
            type="primary"
            label={intl.formatMessage({ id: 'common.add' })}
            htmlType="submit"
            form="add-edit-material-form"
            loading={isLoading}
          />
        </>
      }
      wrapClassName="br-add-edit-material-modal"
    >
      <Form ref={formRef} onFinish={onFinish} name="add-edit-material-form">
        <Form.Item
          name="category"
          label={intl.formatMessage({
            id: 'packaging.manage_materials.add_material.labels.category'
          })}
          rules={[{ required: true }]}
        >
          <Select options={MATERIAL_CATEGORIES} />
        </Form.Item>
        <div className="br-add-edit-material-modal__form-row">
          <BRFormInputs
            formRef={formRef}
            showCounter
            showCounterExceed
            maxCount={NAME_MAX_LENGTH}
            name="englishName"
            inputTitle={intl.formatMessage({
              id: 'packaging.manage_materials.add_material.labels.english_name'
            })}
            rules={[
              requiredRule(
                intl.formatMessage(
                  {
                    id: 'form.required_custom'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'packaging.manage_materials.add_material.labels.english_name'
                    })
                  }
                )
              ),
              englishNameRule(
                intl.formatMessage({
                  id: 'packaging.manage_materials.add_material.validation.english_name'
                })
              ),
              {
                max: NAME_MAX_LENGTH,
                message: intl.formatMessage(
                  {
                    id: 'packaging.manage_materials.add_material.validation.max_length'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'packaging.manage_materials.add_material.labels.english_name'
                    }),
                    max: NAME_MAX_LENGTH
                  }
                )
              }
            ]}
          />
          <BRFormInputs
            formRef={formRef}
            name="arabicName"
            inputTitle={intl.formatMessage({
              id: 'packaging.manage_materials.add_material.labels.arabic_name'
            })}
            showCounter
            showCounterExceed
            maxCount={NAME_MAX_LENGTH}
            rules={[
              requiredRule(
                intl.formatMessage(
                  {
                    id: 'form.required_custom'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'packaging.manage_materials.add_material.labels.arabic_name'
                    })
                  }
                )
              ),
              arabicNameRule(
                intl.formatMessage({
                  id: 'packaging.manage_materials.add_material.validation.arabic_name'
                })
              ),
              {
                max: NAME_MAX_LENGTH,
                message: intl.formatMessage(
                  {
                    id: 'packaging.manage_materials.add_material.validation.max_length'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'packaging.manage_materials.add_material.labels.arabic_name'
                    }),
                    max: NAME_MAX_LENGTH
                  }
                )
              }
            ]}
          />
        </div>

        <BRFormInputs
          formRef={formRef}
          name="englishDescription"
          inputTitle={intl.formatMessage({
            id: 'packaging.manage_materials.add_material.labels.english_description'
          })}
          showCounter
          showCounterExceed
          maxCount={DESCRIPTION_MAX_LENGTH}
          rules={[
            requiredRule(
              intl.formatMessage(
                {
                  id: 'form.required_custom'
                },
                {
                  label: intl.formatMessage({
                    id: 'packaging.manage_materials.add_material.labels.english_description'
                  })
                }
              )
            ),
            englishNameRule(
              intl.formatMessage({
                id: 'packaging.manage_materials.add_material.validation.english_description'
              })
            ),
            {
              max: DESCRIPTION_MAX_LENGTH,
              message: intl.formatMessage(
                {
                  id: 'packaging.manage_materials.add_material.validation.max_length'
                },
                {
                  label: intl.formatMessage({
                    id: 'packaging.manage_materials.add_material.labels.english_description'
                  }),
                  max: DESCRIPTION_MAX_LENGTH
                }
              )
            }
          ]}
        />
        <BRFormInputs
          formRef={formRef}
          name="arabicDescription"
          inputTitle={intl.formatMessage({
            id: 'packaging.manage_materials.add_material.labels.arabic_description'
          })}
          showCounter
          showCounterExceed
          maxCount={DESCRIPTION_MAX_LENGTH}
          rules={[
            requiredRule(
              intl.formatMessage(
                {
                  id: 'form.required_custom'
                },
                {
                  label: intl.formatMessage({
                    id: 'packaging.manage_materials.add_material.labels.arabic_description'
                  })
                }
              )
            ),
            arabicNameRule(
              intl.formatMessage({
                id: 'packaging.manage_materials.add_material.validation.arabic_description'
              })
            ),
            {
              max: DESCRIPTION_MAX_LENGTH,
              message: intl.formatMessage(
                {
                  id: 'packaging.manage_materials.add_material.validation.max_length'
                },
                {
                  label: intl.formatMessage({
                    id: 'packaging.manage_materials.add_material.labels.arabic_description'
                  }),
                  max: DESCRIPTION_MAX_LENGTH
                }
              )
            }
          ]}
        />
        <div className="br-add-edit-material-modal__form-row">
          <BRFormInputs
            formRef={formRef}
            name="packQuantity"
            inputTitle={intl.formatMessage({
              id: 'packaging.manage_materials.add_material.labels.pack_qty'
            })}
            type={NUMBER_TEXT}
            rules={[
              requiredRule(
                intl.formatMessage(
                  {
                    id: 'form.required_custom'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'packaging.manage_materials.add_material.labels.pack_qty'
                    })
                  }
                )
              )
            ]}
          />

          <BRFormInputs
            formRef={formRef}
            name="packPrice"
            inputTitle={
              <div className="br-add-edit-material-modal__form-item-title">
                {intl.formatMessage({
                  id: 'packaging.manage_materials.add_material.labels.price_per_pack'
                })}
                <Tooltip
                  title={intl.formatMessage({
                    id: 'packaging.manage_materials.add_material.tooltips.price_per_pack'
                  })}
                >
                  <TooltipIcon />
                </Tooltip>
              </div>
            }
            type={NUMBER_TEXT}
            rules={[
              requiredRule(
                intl.formatMessage(
                  {
                    id: 'form.required_custom'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'packaging.manage_materials.add_material.labels.price_per_pack'
                    })
                  }
                )
              )
            ]}
            prefix={getCurrency().localized}
          />
        </div>

        <BRFormInputs
          formRef={formRef}
          name="maxLimit"
          inputTitle={
            <div className="br-add-edit-material-modal__form-item-title">
              {intl.formatMessage({
                id: 'packaging.manage_materials.add_material.labels.set_order_limit'
              })}
              <Tooltip
                title={intl.formatMessage({
                  id: 'packaging.manage_materials.add_material.tooltips.max_limit'
                })}
              >
                <TooltipIcon />
              </Tooltip>
            </div>
          }
          type={NUMBER_TEXT}
          rules={[
            requiredRule(
              intl.formatMessage(
                {
                  id: 'form.required_custom'
                },
                {
                  label: intl.formatMessage({
                    id: 'packaging.manage_materials.add_material.labels.set_order_limit'
                  })
                }
              )
            )
          ]}
        />

        <Form.Item
          name="imageKey"
          label={intl.formatMessage({
            id: 'packaging.manage_materials.add_material.labels.icon_image'
          })}
          rules={[{ required: true }]}
        >
          <UploadAttachment
            accept=".jpg, .jpeg, .png"
            maxCount={1}
            maxLimit={10}
            hideUploadButtonAfterMaxCount
            customSubtitle={intl.formatMessage({
              id: 'packaging.manage_materials.add_material.labels.image_title'
            })}
            showUploadList={{
              showPreviewIcon: false
            }}
            disableEncoding
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditMaterialModal;
